import { isModifierKeyPressed } from '@databases/react';
import { noop } from 'lodash-es';
import React, { type FunctionComponent, type ReactNode, useCallback } from 'react';

import { useFormRequired } from './FormProvider';

export function submitFormOnEnter<
  EventElement extends HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
>(e: React.KeyboardEvent<EventElement>) {
  if (!isModifierKeyPressed(e) && e.key === 'Enter') {
    e.preventDefault();
    if (e.currentTarget.form) {
      if (e.currentTarget.form.requestSubmit) {
        e.currentTarget.form.requestSubmit();
      } else {
        // Safari workaround
        e.currentTarget.form?.dispatchEvent(new Event('submit'));
      }
    }
  }
}

interface FormProps {
  children: ReactNode;
  onSubmit?: () => void | Promise<void>;
  className?: string;
}

/**
 * Renders a form element whose submit handler will submit the Form provided by FormProvider.
 */
const Form: FunctionComponent<FormProps> = ({ className, children, onSubmit = noop }) => {
  const form = useFormRequired();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      await form.submit(onSubmit); // If onSubmit is undefined, this will at least call the submit interceptors
    },
    [form, onSubmit]
  );

  return (
    <form className={className} onSubmit={handleSubmit}>
      {children}
    </form>
  );
};

export default Form;
